import { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { addAlpha, Colors } from "../../../../utils/colors";
import { TextComposerAssistType } from "@markit/common.types";
import { API } from "../../../../API";
import {
  TextComposerSuggestion,
  TextComposerSuggestionResult,
} from "./TextComposerSuggestion";
import TextHoverButton from "../../../Buttons/TextHoverButton";

// Both the expand and minimize transitions slide left-to-right on mount.
// Deliberately no exit animation: the outgoing panel is removed instantly
// and only the incoming one animates, so only one panel ever occupies
// layout space (an earlier fade-out overlay version verified as jank-free
// in the browser, but wasn't worth the added visual noise - simpler reads
// better).
const SLIDE_IN_TRANSITION = {
  initial: { opacity: 0, x: -16 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.15, ease: "easeOut" as const },
};

type AssistOption = {
  type: TextComposerAssistType;
  label: string;
  icon: string;
};

const ASSIST_OPTIONS: AssistOption[] = [
  { type: "prettify", label: "Make it Pretty", icon: "ion:sparkles" },
  { type: "saveCredits", label: "Reduce Credits", icon: "ion:prism" },
  {
    type: "textLikeAFriend",
    label: "Text Like a Friend",
    icon: "mdi:account-heart-outline",
  },
  { type: "engagingHook", label: "Engaging Hook", icon: "mdi:hook" },
];

// Shared with ConfirmSendTextModal so the send-modal nudge can name the
// specific assist ("re-run Reduce Credits") using the same label a creator
// already saw on the chip.
export const TEXT_COMPOSER_ASSIST_LABELS: Record<
  TextComposerAssistType,
  string
> = ASSIST_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.type]: option.label }),
  {} as Record<TextComposerAssistType, string>
);

type TextComposerCardProps = {
  draftText: string;
  eventRefId?: string;
  onApplySuggestion: (
    rewrittenText: string,
    assistType: TextComposerAssistType
  ) => void;
};

// Suggest, don't auto-apply - selecting a chip only ever produces a preview
// below; the draft itself changes only when the creator taps "Use this".
export const TextComposerCard = (props: TextComposerCardProps) => {
  const { draftText, eventRefId, onApplySuggestion } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeAssist, setActiveAssist] =
    useState<TextComposerAssistType | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const [suggestion, setSuggestion] =
    useState<TextComposerSuggestionResult | null>(null);

  const resetSuggestion = useCallback(() => {
    setActiveAssist(null);
    setStatus("idle");
    setSuggestion(null);
  }, []);

  const onChipPress = useCallback(
    async (assistType: TextComposerAssistType) => {
      if (status === "loading") {
        return;
      }

      if (activeAssist === assistType) {
        // Pressing the already-selected chip again closes its suggestion.
        resetSuggestion();
        return;
      }

      if (!draftText.trim()) {
        return;
      }

      setActiveAssist(assistType);
      setStatus("loading");
      setSuggestion(null);

      try {
        const response = await API.text.textComposer({
          draftText,
          assistType,
          eventRefId,
        });
        if (response.success && response.rewrittenText) {
          setSuggestion({ rewrittenText: response.rewrittenText });
          setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Error getting text composer suggestion:", error);
        setStatus("error");
      }
    },
    [draftText, eventRefId, status, activeAssist, resetSuggestion]
  );

  const onMinimize = useCallback(() => {
    setIsExpanded(false);
    resetSuggestion();
  }, [resetSuggestion]);

  const onUseThis = useCallback(() => {
    if (suggestion && activeAssist) {
      onApplySuggestion(suggestion.rewrittenText, activeAssist);
    }
    resetSuggestion();
  }, [suggestion, activeAssist, onApplySuggestion, resetSuggestion]);

  const renderCollapsed = (onPress: () => void) => (
    <div
      className="AlignedRowSelect"
      style={{
        gap: 6,
        padding: "7px 14px",
        borderRadius: 8,
        border: `1px solid ${Colors.PINK2}`,
      }}
      onClick={onPress}
    >
      <Icon icon="ion:sparkles" height={14} style={{ color: Colors.PINK2 }} />
      <span className="bodySmallMedium" style={{ color: Colors.PINK2 }}>
        Help me write
      </span>
    </div>
  );

  const renderExpanded = (onMinimize: () => void) => (
    <div className="ColumnNormal" style={{ gap: 10 }}>
      <div
        className="AlignedRowSelect"
        style={{ gap: 4, alignSelf: "flex-end" }}
      >
        <TextHoverButton
          text="Help me write"
          icon={
            <Icon
              icon="ion:sparkles"
              height={13}
              style={{ color: Colors.PINK2 }}
            />
          }
          iconPosition="left"
          onPress={onMinimize}
          textStyles={{ color: Colors.PINK2, fontWeight: 600 }}
        />
      </div>
      <div
        className="AlignedRow"
        style={{ gap: 7, overflowX: "auto", width: "100%" }}
      >
        {ASSIST_OPTIONS.map((option) => {
          const isActive = activeAssist === option.type;
          const isLoading = isActive && status === "loading";
          const isSelectedReady = isActive && status === "ready";
          return (
            <div
              key={option.type}
              className="AlignedRowSelect"
              style={{
                gap: 4,
                padding: "7px 10px",
                borderRadius: 10,
                border: `1px solid ${
                  isSelectedReady ? Colors.PINK2 : Colors.GRAY2
                }`,
                backgroundColor: isSelectedReady
                  ? addAlpha(Colors.PINK2, 0.1)
                  : Colors.WHITE,
                color: isSelectedReady ? Colors.PINK2 : Colors.BLACK,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onClick={() => onChipPress(option.type)}
            >
              {isLoading ? (
                <CircularProgress size={12} style={{ color: Colors.PINK2 }} />
              ) : (
                <Icon icon={option.icon} height={13} />
              )}
              {option.label}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="ColumnNormal" style={{ gap: 10 }}>
      <LazyMotion features={domAnimation}>
        {!isExpanded ? (
          <m.div
            key="collapsed"
            {...SLIDE_IN_TRANSITION}
            style={{ alignSelf: "flex-start" }}
          >
            {renderCollapsed(() => setIsExpanded(true))}
          </m.div>
        ) : (
          <m.div key="expanded" {...SLIDE_IN_TRANSITION}>
            {renderExpanded(onMinimize)}
          </m.div>
        )}
      </LazyMotion>
      {status === "ready" && suggestion ? (
        <TextComposerSuggestion
          suggestion={suggestion}
          onUseThis={onUseThis}
          onKeepMine={resetSuggestion}
        />
      ) : status === "error" ? (
        <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
          Couldn&apos;t generate a suggestion right now - try again in a moment.
        </span>
      ) : null}
    </div>
  );
};
